import { createContext, useCallback, useMemo, useState } from 'react';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { CacheTags, userHasClaim } from 'types';
import { selectUser } from './authSlice';
import type { AppDispatch, RootState } from './store';
import { useToken } from '@chakra-ui/react';
import { TransactionType } from 'api';
import { ClientResponse } from 'api/bindings/ClientResponse';

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAuth = () => {
  const context = useSelector(selectUser);
  return useMemo(() => context, [context]);
};

export interface PageSettings {
  page: number;
  size: number;
}
export type PageSetter = (p: PageSettings) => void;

export const usePagination = (): [PageSettings, PageSetter] => {
  const [sp, setSp] = useSearchParams({
    page: '0',
    size: '20',
  });

  const result = useMemo(() => {
    return {
      page: parseInt(sp.get('page') || '0'),
      size: parseInt(sp.get('size') || '20'),
    };
  }, [sp]);

  const setter = (p: PageSettings) =>
    setSp({
      page: p.page.toString(),
      size: p.size.toString(),
    });

  return [result, setter];
};

export interface PaginatedResults<T> extends PageSettings {
  itemCount: number;
  pageCount: number;
  items: T[];
}

export const useSlicePagination = <T>(
  items: T[] | undefined | null,
  initialSettings?: PageSettings
): [PaginatedResults<T>, PageSetter] => {
  const [settings, setSettings] = useState<PageSettings>(
    initialSettings ?? { page: 0, size: 20 }
  );
  const itemLength = useMemo(() => items?.length || 0, [items?.length]);

  const pageCount = useMemo(
    () => Math.max(1, Math.floor(itemLength / settings.size)),
    [itemLength, settings.size]
  );

  const result = useMemo(() => {
    const start = settings.page * settings.size;
    const end = start + settings.size;
    const slice = items?.slice(start, end) ?? [];
    return {
      items: slice,
      itemCount: itemLength,
      size: settings.size,
      page: settings.page,
      pageCount,
    };
  }, [itemLength, items, pageCount, settings.page, settings.size]);

  const setter = useCallback(
    (settings: PageSettings) => setSettings(settings),
    [setSettings]
  );
  return [result, setter];
};
export interface SortSettings {
  columnIndex: number;
  direction?: 'asc' | 'desc';
}

export const useSort = useState<SortSettings>;

export const sortDateStrings =
  <T = any>(f: (v: T) => string | Date) =>
  (l: T, r: T) => {
    return new Date(f(l)).getTime() - new Date(f(r)).getTime();
  };

export const useClaim = (claim: string, value: string) => {
  const auth = useAuth();
  return useMemo(
    () => userHasClaim(auth?.user, claim, value),
    [auth, claim, value]
  );
};

export const useAuthZ = (): [
  string | undefined,
  (c: string, v: any) => boolean
] => {
  const auth = useAuth();
  return useMemo(() => {
    return [
      auth?.user.id,
      (c: string, v: any) =>
        !!auth?.user.claims.find((x) => x.claim.name === c && x.value === v),
    ];
  }, [auth]);
};

export const useAdmin = (): [string | undefined, boolean] => {
  const [userId, hasClaim] = useAuthZ();
  return useMemo(() => {
    const isSysAdmin = hasClaim('sa', '1');
    const isClientAdmin = hasClaim('ca', '1');
    return [userId, isClientAdmin || isSysAdmin];
  }, [hasClaim, userId]);
};

export function getCacheTagType(tt: TransactionType): string {
  switch (tt) {
    case TransactionType.Afe:
      return CacheTags.afeDetail;
    case TransactionType.PurchaseOrder:
      return CacheTags.purchaseOrderDetail;
    case TransactionType.WorkOrder:
      return CacheTags.workOrderDetail;
    case TransactionType.Invoice:
      return CacheTags.invoiceDetail;
    default:
      throw new Error('unrecognized txn type');
  }
}

export const ClientContext = createContext<ClientResponse>({} as any); // we are getting away with this because the provider hides children until client is loaded

export const useProgressBarColors = () =>
  useToken('colors', ['red.200', 'purple.200', 'orange.200', 'yellow.200']);
